import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { scaleLinear } from "d3-scale";
import { extent } from "d3-array";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { TCatalogEntities } from "types";

import Pipeline from "../Pipeline";
import AutoComplete from "../Autocomplete";
import Filter from "../../Filter";

import KilometerReducer from "./KilometerReducer";
import KilometerIndicator from "./KilometerIndicator";

interface IInfo {
  value: [number, number] | undefined;
  range: [number, number];
}

const Info: React.FC<IInfo> = ({ value, range }): JSX.Element => {
  let id = "common.kilometer.span";
  let values = null;
  if (!value) {
    values = { from: range[0], to: range[1] };
  } else if (value[0] === value[1]) {
    id = "common.kilometer.plural";
    values = { count: value[0] };
  } else {
    values = { from: value[0], to: value[1] };
  }

  return (
    <Typography sx={{ mb: 2 }} variant="body1" color="secondary">
      <FormattedMessage {...{ id, values }} />
    </Typography>
  );
};

interface IFilter {
  entities: TCatalogEntities;
  value: number[];
  dispatchFilter?: any;
}

export const SideFilter: React.FC<IFilter> = ({
  entities: { vehicle: vehicles },
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const [maximum, setMaximum]: [number | undefined, any] = useState();
  const [minimum, setMinimum]: [number | undefined, any] = useState();
  const [range, setRange]: [[number, number] | undefined, any] = useState();
  React.useEffect(() => {
    if (!vehicles) return;
    const edges: [any, any] = extent(Object.values(vehicles), (v) => v.km);
    const scale = scaleLinear().domain(edges).range([0, 1]).nice();
    setRange([scale.invert(0), scale.invert(1)]);
  }, [vehicles, dispatchFilter]);

  React.useEffect(
    () => {
      if (!(vehicles && range)) return;
      if (!value) {
        setMinimum(range[0]);
        setMaximum(range[1]);
      } else {
        setMinimum(value[0]);
        setMaximum(value[1]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, range]
  );

  const handleChange = (element: any, newValue: any) => {
    setMinimum(newValue[0]);
    setMaximum(range && newValue[1] > range[1] ? range[1] : newValue[1]);
  };

  const submit = (element: any, value: any) => {
    const newValue =
      JSON.stringify(range) === JSON.stringify(value) ? null : value;
    dispatchFilter({
      type: "UPDATE_FILTER",
      payload: { kilometer: newValue },
    });
  };

  if (minimum === undefined || maximum === undefined || !range) return null;

  const Component = (
    <Box sx={{ px: 1 }}>
      <Info value={[minimum, maximum]} range={range} />
      <AutoComplete
        value={[minimum, maximum]}
        {...{
          range,
          onChangeHandler: handleChange,
          onChangeCommittedHandler: submit,
        }}
      />
    </Box>
  );

  return (
    <Filter
      component={Component}
      open={false}
      title={
        <FormattedMessage
          id="filter.kilometer.label"
          defaultMessage="Kilometer"
        />
      }
    />
  );
};

export const SimpleFilter: React.FC<IFilter> = ({
  entities: { vehicle: vehicles },
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t, formatNumber: n } = useIntl();
  const [range, setRange]: [[number, number] | undefined, any] = useState();

  React.useEffect(() => {
    if (!vehicles) return;
    const edges: [any, any] = extent(Object.values(vehicles), (v) => v.km);
    const scale = scaleLinear().domain(edges).range([0, 1]).nice();
    const range = [scale.invert(0), scale.invert(1)];
    setRange(range);
  }, [vehicles]);

  if (!range) return null;

  const setValueHandler = (event: SelectChangeEvent) => {
    // Check if all variants are selected
    const value = parseInt(event.target.value);
    const newValue = value === 0 ? null : [range[0], value];
    dispatchFilter({ type: "UPDATE_FILTER", payload: { kilometer: newValue } });
  };

  var options = [];
  for (var i = range[0] / 10000; i <= range[1] / 10000; i++) {
    options.push({
      value: i * 10000,
      title: `${n(i * 10000)}km`,
    });
  }
  options.shift();

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  return (
    <FormControl size="small">
      <InputLabel id="filter-km-simple-label">
        <FormattedMessage id="filter.km-to.label" defaultMessage="bis km" />
      </InputLabel>
      <Select
        labelId="filter-km-simple-label"
        id="filter-km-simple"
        value={!value ? "0" : value[1].toString()}
        label={t({
          id: "filter.km-to.label",
          defaultMessage: "bis km",
        })}
        onChange={setValueHandler}
      >
        <MenuItem value={0}>{all.title}</MenuItem>
        {options.map(({ value, title }) => (
          <MenuItem key={"km-" + value} value={value}>
            {title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface IContainer {
  Pipe?: any;
  variant?: string;
}

const Container: React.FC<IContainer> = ({
  Pipe = Pipeline,
  variant = "side",
}) => {
  const Filter = variant === "side" ? SideFilter : SimpleFilter;
  return (
    <Pipe
      {...{
        defaultValue: null,
        type: "kilometer",
        Component: Filter,
        Reducer: KilometerReducer,
        Indicator: KilometerIndicator,
      }}
    />
  );
};

export default Container;
